import { createContext, useContext, type ReactNode, type PropsWithChildren } from 'react';
import camelcaseKeys from 'camelcase-keys';
import type { Jsonifiable } from 'type-fest';
import type { LDClient } from 'launchdarkly-react-client-sdk';

interface LDWindow {
  __LD__?: {
    _client?: LDClient;
    isLocal: boolean;
  };
}

export type FlagSet = Record<string, NonNullable<Jsonifiable>>;

export const FlagsContext = createContext<FlagSet | null>(null);

interface FlagsProviderProps extends PropsWithChildren {
  flags: FlagSet;
}

export function FlagsProvider({ children, flags }: FlagsProviderProps): ReactNode {
  return <FlagsContext.Provider value={flags}>{children}</FlagsContext.Provider>;
}

export const useFlags = (): FlagSet => {
  const allFlags = useContext(FlagsContext);

  if (!allFlags) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  const camelcased = camelcaseKeys(allFlags);

  const reverseMap: Record<string, string> = {};
  for (const originalKey of Object.keys(allFlags)) {
    const camelKey = Object.keys(camelcaseKeys({ [originalKey]: true }))[0]!;
    reverseMap[camelKey] = originalKey;
  }

  return new Proxy(camelcased, {
    get(target, prop) {
      if (typeof prop !== 'string') return Reflect.get(target, prop);

      const originalKey = reverseMap[prop];
      const ldWindow = window as unknown as LDWindow;
      if (originalKey !== undefined && ldWindow.__LD__?.isLocal === false) {
        try {
          ldWindow.__LD__._client?.variation(originalKey);
        } catch (error) {
          console.warn('Failed to report LaunchDarkly variation', error);
        }
      }

      return target[prop];
    },
  });
};
